import type { GridProps, HTMLChakraProps } from '@chakra-ui/react';
import { Box, Grid, Flex, Text, VStack, Skeleton, Heading } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import type { IconName } from 'public/icons/name';
import React from 'react';

import type { CustomLinksGroup } from 'types/footerLinks';

import config from 'configs/app';
import type { ResourceError } from 'lib/api/resources';
// import useApiQuery from 'lib/api/useApiQuery';
import useFetch from 'lib/hooks/useFetch';
// import useIssueUrl from 'lib/hooks/useIssueUrl';
// import { copy } from 'lib/html-entities';
import { CONTENT_MAX_WIDTH } from 'ui/shared/layout/utils';
import NetworkAddToWallet from 'ui/shared/NetworkAddToWallet';

import FooterLinkItem from './FooterLinkItem';
import IntTxsIndexingStatus from './IntTxsIndexingStatus';

// import getApiVersionUrl from './utils/getApiVersionUrl';

const MAX_LINKS_COLUMNS = 4;

// const FRONT_VERSION_URL = `https://github.com/blockscout/frontend/tree/${ config.UI.footer.frontendVersion }`;
// const FRONT_COMMIT_URL = `https://github.com/blockscout/frontend/commit/${ config.UI.footer.frontendCommit }`;

type FooterLinkProps = {
  icon?: IconName;
  iconSize?: string;
  text: string;
  url: string;
  isLoading?: boolean;
};
const BLOCKSCOUT_LINKS = [
  // Our Community
  {
    title: 'Bahamut',
    links: [
      {
        text: 'Home',
        url: 'https://www.bahamut.io/',
      },
      {
        text: 'Ecosystem',
        url: 'https://www.bahamut.io/bahamut-infrastructure',
      },
      {
        text: 'Documentation',
        url: 'https://docs.bahamut.io/',
      },
    ],
  },
  {
    title: 'Socials',
    links: [
      {
        icon: 'social/telega' as const,
        iconSize: '18px',
        text: 'Telegram',
        url: 'https://t.me/fasttoken_eng',
      },
      {
        icon: 'social/twitter' as const,
        iconSize: '18px',
        text: 'X (ex-Twitter)',
        url: 'https://x.com/fasttoken_com/',
      },
      {
        icon: 'social/discord' as const,
        iconSize: '20px',
        text: 'Discord',
        url: 'https://discord.gg/CzGp8gDrFW',
      },
    ],
  },
  // Our Socials

  // {
  //   icon: 'social/linkedin' as const,
  //   iconSize: '24px',
  //   text: 'Discord',
  //   url: 'https://discord.gg/blockscout',
  // },
  /*  {
      icon: 'brands/blockscout' as const,
      iconSize: '18px',
      text: 'All chains',
      url: 'https://www.blockscout.com/chains-and-projects',
    },
    {
      icon: 'donate' as const,
      iconSize: '20px',
      text: 'Donate',
      url: 'https://github.com/sponsors/blockscout',
    },*/
];

const Footer = () => {

  // const { data: backendVersionData } = useApiQuery('config_backend_version', {
  //   queryOptions: {
  //     staleTime: Infinity,
  //   },
  // });
  // const apiVersionUrl = getApiVersionUrl(backendVersionData?.backend_version);
  // const issueUrl = useIssueUrl(backendVersionData?.backend_version);

  // const frontendLink = (() => {
  //   if (config.UI.footer.frontendVersion) {
  //     return <Link href={ FRONT_VERSION_URL } target="_blank">{ config.UI.footer.frontendVersion }</Link>;
  //   }

  //   if (config.UI.footer.frontendCommit) {
  //     return <Link href={ FRONT_COMMIT_URL } target="_blank">{ config.UI.footer.frontendCommit }</Link>;
  //   }

  //   return null;
  // })();

  const fetch = useFetch();

  const { isPlaceholderData, data: linksData } = useQuery<unknown, ResourceError<unknown>, Array<CustomLinksGroup>>({
    queryKey: [ 'footer-links' ],
    queryFn: async() => fetch(config.UI.footer.links || '', undefined, { resource: 'footer-links' }),
    enabled: Boolean(config.UI.footer.links),
    staleTime: Infinity,
    placeholderData: [],
  });

  const colNum: number = isPlaceholderData ? 1 : Math.min(linksData?.length || Infinity, MAX_LINKS_COLUMNS) + 1;

  const renderProjectInfo = React.useCallback((gridArea?: GridProps['gridArea']) => {
    return (
      <Box gridArea={ gridArea }>
        <Text mt={ 3 } fontSize="xs">
          Ftnscan is a tool for inspecting and analyzing EVM based blockchains. Blockchain explorer for Bahamut Networks.
        </Text>
        {/* <Box mt={ 6 } alignItems="start" fontSize="xs" lineHeight={ 5 }>
          { apiVersionUrl && (
            <Text>
              Backend: <Link href={ apiVersionUrl } target="_blank">{ backendVersionData?.backend_version }</Link>
            </Text>
          ) }
          { frontendLink && (
            <Text>
              Frontend: { frontendLink }
            </Text>
          ) }
          <Text>
            Copyright { copy } Fastex Limited 2023-{ (new Date()).getFullYear() }
          </Text>
        </Box> */}
      </Box>
    );
  }, []);

  const renderNetworkInfo = React.useCallback((gridArea?: GridProps['gridArea']) => {
    return (
      <Flex
        gridArea={ gridArea }
        flexWrap="wrap"
        columnGap={ 8 }
        rowGap={ 6 }
        mb={{ base: 5, lg: 10 }}
        _empty={{ display: 'none' }}
      >
        { !config.UI.indexingAlert.intTxs.isHidden && <IntTxsIndexingStatus/> }
        <NetworkAddToWallet/>
        {renderProjectInfo()}
      </Flex>
    );
  }, [ renderProjectInfo ]);

  const containerProps: HTMLChakraProps<'footer'> = {
    as: 'footer',
    borderTopWidth: '1px',
    borderTopColor: 'solid',
  };

  const contentProps: GridProps = {
    px: { base: 6, lg: config.UI.navigation.layout === 'horizontal' ? 6 : 14, '2xl': 16 },
    py: { base: 3, lg: 4 },
    gridTemplateColumns: { base: '1fr', lg: 'minmax(auto, 470px) 1fr' },
    columnGap: { lg: '32px', xl: '100px' },
    maxW: `${ CONTENT_MAX_WIDTH }px`,
    m: '0 auto',
  };

  if (config.UI.footer.links) {
    // @ts-ignore
    // @ts-ignore
    // @ts-ignore
    // @ts-ignore
    return (
      <Box { ...containerProps }>
        <Grid { ...contentProps }>
          <div>
            { renderNetworkInfo() }
          </div>

          <Grid
            gap={{ base: 6, lg: colNum === MAX_LINKS_COLUMNS + 1 ? 2 : 8, xl: 12 }}
            gridTemplateColumns={{
              base: 'repeat(auto-fill, 160px)',
              lg: `repeat(${ colNum }, 135px)`,
              xl: `repeat(${ colNum }, 160px)`,
            }}
            justifyContent={{ lg: 'flex-end' }}
            mt={{ base: 8, lg: 0 }}
          >
            {
              ([
                { title: 'Bahamut', links: BLOCKSCOUT_LINKS },
                ...(linksData || []),
              ])
                .slice(0, colNum)
                .map(linkGroup => (
                  <Box key={ linkGroup.title }>
                    <Skeleton fontWeight={ 500 } mb={ 3 } display="inline-block" isLoaded={ !isPlaceholderData }>{ linkGroup.title }</Skeleton>
                    <VStack spacing={ 1 } alignItems="start">
                      {linkGroup.links.map((link) => {
                        const { url, text } = (link as FooterLinkProps);

                        return (
                          <FooterLinkItem
                            key={text || url}
                            {...link as FooterLinkProps}
                            isLoading={isPlaceholderData}/>
                        );
                      })
                      }
                    </VStack>
                  </Box>
                ))
            }
          </Grid>
        </Grid>
      </Box>
    );
  }

  return (
    <Box {...containerProps}>
      <Grid
        {...contentProps}
        gridTemplateAreas={{
          lg: `
          "network links-top"
          "info links-bottom"
        `,
        }}
      >
        {renderNetworkInfo({ lg: 'network' })}
        <Flex
          gap={14}
          flexDir={{ md: 'row', base: 'column' }}
          justifyContent="flex-end"
          mt={{ base: 8, lg: 0 }}
        >
          {BLOCKSCOUT_LINKS.map(({ title, links }) => (
            <Grid key={ title } gap="0">
              <Heading size="xm">{ title }</Heading>
              <Grid
                gap={1}
                gridAutoFlow={{ base: 'row', lg: 'column' }}
                mt={{ base: 8, lg: 0 }}
                gridTemplateRows="repeat(3, auto)"
              >
                {links.map((item) => (
                  <FooterLinkItem key={item.url || item.text} { ...item } />
                ))}
              </Grid>
            </Grid>
          ))}
        </Flex>
      </Grid>
    </Box>
  );
};

export default React.memo(Footer);
